import React, { useRef } from 'react';
import './cro-sprint.css';

import useScrollReveal from '../hooks/useScrollReveal';
import usePageMeta from '../hooks/usePageMeta';

import SprintHero from '../components/cro/sprint-hero';
import SprintTextSection from '../components/cro/sprint-text-section';
import SprintFeatureGrid from '../components/cro/sprint-feature-grid';
import SprintProcessGrid from '../components/cro/sprint-process-grid';
import SprintTags from '../components/cro/sprint-tags';
import SprintFlexCards from '../components/cro/sprint-flex-cards';
import SprintPillCards from '../components/cro/sprint-pill-cards';
import SprintOutcomes from '../components/cro/sprint-outcomes';
import SprintCta from '../components/cro/sprint-cta';

import {
    IconEye,
    IconMousePointer,
    IconTarget,
    IconSmartphone,
    IconShoppingCart,
    IconFileText,
    IconSearch,
    IconClipboard,
    IconLayers,
    IconFlask,
    IconTool,
    IconBarChart,
    IconDatabase,
    IconZap,
    IconHeart,
    IconTrendingUp,
    IconShoppingBag,
    IconBox,
    IconRefreshCw,
    IconGlobe,
    IconMonitor,
    IconDollarSign,
    IconCheckCircle,
    IconArrowUp
} from '../components/sprint-icons';

export default function CroSprint() {
    const pageRef = useRef(null);

    useScrollReveal(pageRef);
    usePageMeta(
        'CRO Sprint: Fast Conversion Audits & Fixes | Spinnennetz',
        'Accelerate your revenue with a focused CRO Sprint. We identify and fix high-impact conversion bottlenecks in weeks — no long-term contracts or delays needed.',
        '/cro-sprint',
        { "@context": "https://schema.org", "@type": "Service", "name": "CRO Sprint", "provider": { "@type": "Organization", "name": "Spinnennetz" } }
    );

    const includedItems = [
        { icon: <IconEye />, title: 'UX Audit', desc: 'A comprehensive review of your website\'s user experience, navigation, messaging, trust signals, and conversion flow.' },
        { icon: <IconMousePointer />, title: 'Heatmap & Session Analysis', desc: 'Understand how visitors interact with your site through click maps, scroll maps, and user session recordings.' },
        { icon: <IconTarget />, title: 'A/B Testing Strategy', desc: 'Identify high-impact testing opportunities and create experiments designed to improve key conversion metrics.' },
        { icon: <IconSmartphone />, title: 'Mobile Optimization', desc: 'Optimize the mobile experience to reduce friction and improve engagement across all devices.' },
        { icon: <IconShoppingCart />, title: 'Checkout Optimization', desc: 'Analyze and streamline the checkout process to reduce cart abandonment and increase completed purchases.' },
        { icon: <IconFileText />, title: 'Conversion Opportunity Report', desc: 'Receive a prioritized roadmap of recommendations based on potential business impact.' },
    ];

    const processSteps = [
        { num: 1, icon: <IconSearch />, title: 'Data Collection', desc: 'We review analytics, heatmaps, session recordings, funnels, and conversion metrics to establish a clear baseline.' },
        { num: 2, icon: <IconClipboard />, title: 'Conversion Audit', desc: 'We identify friction points, usability issues, and missed conversion opportunities across your entire funnel.' },
        { num: 3, icon: <IconLayers />, title: 'Prioritization', desc: 'Recommendations are ranked based on impact, effort, and expected ROI using proven prioritization frameworks.' },
        { num: 4, icon: <IconFlask />, title: 'Experiment Design', desc: 'We create A/B testing hypotheses and optimization strategies backed by data-driven insights.' },
        { num: 5, icon: <IconTool />, title: 'Implementation Support', desc: 'Our team helps implement approved changes and experiments to ensure quality execution.' },
        { num: 6, icon: <IconBarChart />, title: 'Results & Insights', desc: 'Track performance, analyze outcomes, and uncover the next opportunities for growth.' },
    ];

    const problemsList = [
        'High cart abandonment rates',
        'Low landing page conversion rates',
        'Poor mobile user experience',
        'Checkout drop-offs',
        'Low add-to-cart rates',
        'Weak call-to-action performance',
        'Ineffective product pages',
        'High bounce rates',
        'Low lead generation performance',
    ];

    const whyChooseItems = [
        { icon: <IconDatabase />, title: 'Data-Driven Decisions', desc: 'Every recommendation is backed by user behavior and analytics — not assumptions.' },
        { icon: <IconZap />, title: 'Faster Wins', desc: 'Focus on high-impact improvements that can generate results quickly.' },
        { icon: <IconHeart />, title: 'Customer-Centric Approach', desc: 'Optimize the experience around how real users browse, evaluate, and purchase.' },
        { icon: <IconTrendingUp />, title: 'Measurable Results', desc: 'Track improvements through testing, analytics, and conversion metrics.' },
    ];

    const idealFor = [
        { icon: <IconShoppingBag />, label: 'Shopify Stores' },
        { icon: <IconBox />, label: 'D2C Brands' },
        { icon: <IconRefreshCw />, label: 'Subscription Businesses' },
        { icon: <IconGlobe />, label: 'Lead Generation Websites' },
        { icon: <IconMonitor />, label: 'SaaS Landing Pages' },
        { icon: <IconDollarSign />, label: 'eCommerce Brands Running Paid Ads' },
    ];

    const outcomes = [
        { icon: <IconCheckCircle />, label: 'Higher Conversion Rates' },
        { icon: <IconArrowUp />, label: 'Increased Revenue Per Visitor' },
        { icon: <IconSmartphone />, label: 'Better Mobile Performance' },
        { icon: <IconCheckCircle />, label: 'Reduced Cart Abandonment' },
        { icon: <IconArrowUp />, label: 'Improved Checkout Completion' },
        { icon: <IconTrendingUp />, label: 'More Leads & Sales From Existing Traffic' },
    ];

    const heroMetrics = [
        { icon: <IconTrendingUp />, value: '+42%', label: 'Conversion Rate' },
        { icon: <IconShoppingCart />, value: '−35%', label: 'Cart Abandonment' },
        { icon: <IconSmartphone />, value: '+58%', label: 'Mobile Engagement' },
        { icon: <IconBarChart />, value: '2.4×', label: 'Revenue Per Visitor' },
    ];

    return (
        <div className="cro-sprint-page" ref={pageRef}>
            <SprintHero
                badgeText="CRO Sprint"
                titlePrefix="Improve Conversions"
                highlightText="Without Increasing"
                titleSuffix="Ad Spend"
                subtitle="Getting traffic is expensive. Losing potential customers because of friction, poor user experience, or checkout issues is even more expensive."
                subtitle2="Whether you're running a Shopify store, D2C brand, or lead generation website, we'll help you turn more visitors into customers."
                primaryButtonText="Book Your CRO Sprint"
                secondaryButtonText="What's Included"
                secondaryHref="#whats-included"
                metrics={heroMetrics}
            />

            <SprintTextSection
                label="What Is a CRO Sprint?"
                titlePrefix="A Structured Process for"
                highlightText="Measurable Conversion Gains"
                titleSuffix=""
                paragraphs={[
                    'A CRO Sprint is a structured optimization process that combines user behavior analysis, UX research, experimentation, and performance improvements to deliver measurable conversion gains in a short timeframe.',
                    'Instead of guessing what works, we use data to identify exactly where visitors drop off and what changes will drive better results.'
                ]}
            />

            <SprintFeatureGrid
                sectionId="whats-included"
                label="What's Included"
                titlePrefix="Everything you need to"
                highlightText="boost conversions"
                titleSuffix=""
                items={includedItems}
            />

            <SprintProcessGrid
                label="Our CRO Sprint Process"
                titlePrefix="Six steps to"
                highlightText="higher conversions"
                titleSuffix=""
                steps={processSteps}
            />

            <SprintTags
                label="Common Problems We Solve"
                titlePrefix="Conversion killers we"
                highlightText="eliminate"
                titleSuffix=""
                items={problemsList}
                variant="problem"
            />

            <SprintFlexCards
                label="Why Choose Our CRO Sprint?"
                titlePrefix="What makes us"
                highlightText="different"
                titleSuffix=""
                items={whyChooseItems}
                accentVariant="bottom"
            />

            <SprintPillCards
                label="Ideal For"
                titlePrefix="Built for businesses"
                highlightText="ready to grow"
                titleSuffix=""
                items={idealFor}
                hoverDirection="right"
            />

            <SprintOutcomes
                title="Expected Outcomes"
                items={outcomes}
            />

            <SprintCta
                title={<>Ready to Unlock<br />More Revenue?</>}
                subtitle="Stop leaving conversions on the table. Discover what's preventing visitors from becoming customers and get a clear action plan to improve performance."
                buttonText="Book Your CRO Sprint Today"
            />
        </div>
    );
}
